import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import useModalStore from '@/stores/useModalStore';
import useConnectWithX from '@/hooks/useConnectWithX';
import useConnectWithGoogle from '@/hooks/useConnectWithGoogle';
import { trackEvent } from '@/utils/analytics';
import { EventAction } from '@/types/analytics';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { setForceCreateMode } from '@/lib/oauth';
import { useToast } from '@/hooks/use-toast';
import IconCross from '@/assets/icons/cross.svg';

const AccountExistsModal: React.FC = () => {
  const { accountExists, setAccountExistsModal } = useModalStore();
  const { show: showModal, provider, existingProvider, email } = accountExists;
  const connectWithX = useConnectWithX().connectWithX;
  const connectWithGoogle = useConnectWithGoogle().connectWithGoogle;
  const { toast } = useToast();

  const handleClose = () => {
    trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
      label: 'account_exists_cancel',
      component: 'account_exists_modal',
    });
    setAccountExistsModal({ show: false });
  };

  const handleLoginWithExisting = async () => {
    trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
      label: `account_exists_login_${existingProvider}`,
      component: 'account_exists_modal',
    });
    
    setAccountExistsModal({ show: false });
    
    // Trigger login with existing provider
    try {
      if (existingProvider === 'x') {
        await connectWithX();
      } else if (existingProvider === 'google') {
        await connectWithGoogle();
      }
    } catch (error) {
      console.error('Failed to initiate login:', error);
    }
  };

  const handleCreateSeparateAccount = async () => {
    trackEvent('ui_interaction', EventAction.BUTTON_CLICK, {
      label: `account_exists_create_separate_${provider}`,
      component: 'account_exists_modal',
    });

    // Set force create mode flag so OAuthCallback knows to use force=true
    if (provider) {
      setForceCreateMode(provider);
    }

    // Close modal
    setAccountExistsModal({ show: false });

    // Re-initiate OAuth flow to get a fresh authorization code
    // (OAuth codes are single-use and the previous one was already consumed)
    try {
      if (provider === 'x') {
        await connectWithX();
      } else if (provider === 'google') {
        await connectWithGoogle();
      }
    } catch (error) {
      console.error('Failed to initiate OAuth flow:', error);
      toast({
        title: "Failed to start OAuth flow",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    }
  };

  if (!showModal || !provider || !existingProvider) return null;

  const attemptedProviderName = provider === 'x' ? 'X/Twitter' : 'Google';
  const existingProviderName = existingProvider === 'x' ? 'X/Twitter' : 'Google';

  return (
    <Dialog open={showModal} onOpenChange={handleClose}>
      <DialogContent className="max-w-md bg-secondary border-gray-800 text-white" shouldShowCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Account Already Exists
            </div>
            <DialogClose asChild>
              <img
                src={IconCross}
                alt="cross"
                className="w-[1.88rem] h-[1.88rem] hover:cursor-pointer"
                onClick={handleClose}
              />
            </DialogClose>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-gray-300">
          <p className="text-base">
            {email ? (
              <>
                The email <strong className="text-white">{email}</strong> is already registered with{' '}
                <strong className="text-white">{existingProviderName}</strong>.
              </>
            ) : (
              <>
                This account is already registered with{' '}
                <strong className="text-white">{existingProviderName}</strong>.
              </>
            )}
          </p>

          <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-sm text-yellow-300">
              What would you like to do?
            </p>
          </div>
        </div>

        <div className="space-y-3 py-2">
          {/* Option 1: Login and link accounts */}
          <div className="p-4 border border-blue-500/30 bg-blue-500/5 rounded-lg">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold">
                A
              </span>
              Login with {existingProviderName} to link accounts
            </h4>
            <p className="text-sm text-gray-300 ml-8">
              Sign in with your existing {existingProviderName} account, then you can link {attemptedProviderName} in Account Management.
            </p>
          </div>

          {/* Option 2: Create separate account */}
          <div className="p-4 border border-gray-700 bg-gray-800/30 rounded-lg">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-600/40 text-gray-300 text-xs font-bold">
                B
              </span>
              Create separate account
            </h4>
            <p className="text-sm text-gray-300 mb-2 ml-8">
              Continue with {attemptedProviderName} as a separate account with a different email.
            </p>
            <p className="text-xs text-yellow-500/80 ml-8 flex items-start gap-1">
              <span>⚠️</span>
              <span>You'll need to manage both accounts separately.</span>
            </p>
          </div>
        </div>

        <div className="flex flex-col xs:flex-row gap-2 mt-4">
          <Button
            onClick={handleLoginWithExisting}
            className="w-full hover:opacity-90 transition-opacity text-[#FFFFFF] font-['Inter'] font-semibold"
            style={{
                background: 'linear-gradient(0deg, #101D4A 0%, #873ED0 100%)',
            }}
          >
            Login with {existingProviderName}
          </Button>

          <Button
            onClick={handleCreateSeparateAccount}
            className="w-full bg-gray-700 hover:bg-gray-600 text-white font-['Inter'] font-semibold"
          >
            Create Separate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountExistsModal;
